import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../components/layout/MainLayout";
import Breadcrumbs from "../components/common/Breadcrumbs";
import Swal from "sweetalert2";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { selectHasPermission } from "../store/slices/authSlice";
import {
  fetchHomeVisitClinicalRecords,
  deleteHomeVisitClinicalRecord,
  clearError,
} from "../store/slices/homeVisitClinicalRecordSlice";

const HomeVisitClinicalRecordHome = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Route state properties
  const { caseId, patientId } = location.state || {};

  const canManageRecord = useSelector((state) =>
    selectHasPermission(state, "home-visit-clinical-record-manage")
  );
  const canCreateRecord = useSelector((state) =>
    selectHasPermission(state, "home-visit-clinical-record-create")
  );
  const canEditRecord = useSelector((state) =>
    selectHasPermission(state, "home-visit-clinical-record-edit")
  );
  const canDeleteRecord = useSelector((state) =>
    selectHasPermission(state, "home-visit-clinical-record-delete")
  );

  const { homeVisitClinicalRecords, isLoading, error } = useSelector((state) => state.homeVisitClinicalRecords);
  const [filterText, setFilterText] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchHomeVisitClinicalRecords(patientId));
  }, [dispatch, patientId]);

  // Actions
  const handleAdd = () => {
    navigate("/home-visitation/clinical-record/add", { state: location.state });
  };

  const handleEdit = (id) => {
    navigate(`/home-visitation/clinical-record/edit/${id}`, { state: location.state });
  };

  const handlePrint = (id) => {
    navigate(`/home-visitation/clinical-record/print/${id}`, { state: location.state });
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this Home Visit Clinical record?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await dispatch(deleteHomeVisitClinicalRecord(id)).unwrap();
        dispatch(fetchHomeVisitClinicalRecords(patientId));
        Swal.fire("Deleted!", "Home Visit Clinical record has been deleted.", "success");
      } catch (err) {
        Swal.fire("Error!", err || "Failed to delete record.", "error");
      }
    }
  };

  // Search filtering
  const filteredRecords = homeVisitClinicalRecords.filter((r) =>
    Object.values(r).some((v) =>
      String(v ?? "")
        .toLowerCase()
        .includes(filterText.toLowerCase())
    )
  );

  const totalRows = filteredRecords.length;
  const start = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const end = Math.min(currentPage * rowsPerPage, totalRows);

  // PDF Export
  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Home Visit Clinical Record Form List", 14, 10);

    const columns = [
      "#",
      "Visit Number",
      "Date of Visit",
      "Duration",
      "Psychiatrist",
      "Rehab Practitioner",
      "Med Compliant",
      "Created At",
    ];

    const rows = filteredRecords.map((r, i) => [
      i + 1,
      r.visit_number ?? "-",
      r.date_of_visit ?? "-",
      r.duration ?? "-",
      r.psychiatrist_name ?? "-",
      r.rehab_practitioner_name ?? "-",
      r.med_adherence ?? "-",
      r.created_at ? new Date(r.created_at).toLocaleDateString() : "-",
    ]);

    autoTable(doc, {
      head: [columns],
      body: rows,
      startY: 20,
    });

    doc.save("home_visit_clinical_records.pdf");
  };

  const CustomPagination = ({
    onChangePage,
    onChangeRowsPerPage,
    currentPage,
    rowsPerPage,
    rowCount,
  }) => {
    const totalPages = Math.max(1, Math.ceil(rowCount / rowsPerPage));
    return (
      <div className="d-flex justify-content-between align-items-center p-3 border-top">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted" style={{ fontSize: "0.875rem" }}>
            Rows per page:
          </span>
          <select
            className="form-select form-select-sm"
            style={{ width: "80px" }}
            value={rowsPerPage}
            onChange={(e) => onChangeRowsPerPage(Number(e.target.value), currentPage)}
          >
            {[10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
        <div className="d-flex align-items-center gap-3">
          <span className="text-muted" style={{ fontSize: "0.875rem" }}>
            {start}-{end} of {rowCount}
          </span>
          <div className="d-flex gap-1">
            <button
              className="btn btn-sm btn-icon btn-outline-secondary"
              disabled={currentPage === 1}
              onClick={() => onChangePage(currentPage - 1)}
            >
              <i className="ri-arrow-left-s-line" />
            </button>
            <button
              className="btn btn-sm btn-icon btn-outline-secondary"
              disabled={currentPage === totalPages}
              onClick={() => onChangePage(currentPage + 1)}
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const columns = [
    {
      name: "Visit #",
      selector: (row) => row.visit_number || "-",
      sortable: true,
    },
    {
      name: "Date of Visit",
      selector: (row) => row.date_of_visit || "-",
      sortable: true,
    },
    {
      name: "Time In / Out",
      selector: (row) =>
        row.time_in && row.time_out ? `${row.time_in} - ${row.time_out}` : "-",
    },
    {
      name: "Psychiatrist",
      selector: (row) => row.psychiatrist_name || "-",
      sortable: true,
    },
    {
      name: "Rehab Practitioner",
      selector: (row) => row.rehab_practitioner_name || "-",
      sortable: true,
    },
    {
      name: "Med Adherence",
      selector: (row) => row.med_adherence || "-",
      sortable: true,
    },
    {
      name: "Created By",
      selector: (row) => row.creator?.name || "-",
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-1">
          {canEditRecord && (
            <button
              className="btn btn-sm btn-icon btn-outline-primary"
              onClick={() => handleEdit(row.id)}
              title="Edit Form"
            >
              <i className="ri-pencil-line" />
            </button>
          )}
          <button
            className="btn btn-sm btn-icon btn-outline-info"
            onClick={() => handlePrint(row.id)}
            title="Print Preview"
          >
            <i className="ri-printer-line" />
          </button>
          {canDeleteRecord && (
            <button
              className="btn btn-sm btn-icon btn-outline-danger"
              onClick={() => handleDelete(row.id)}
              title="Delete Form"
            >
              <i className="ri-delete-bin-line" />
            </button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "150px",
    },
  ];

  // CSV data construction
  const csvHeaders = [
    { label: "Visit Number", key: "visit_number" },
    { label: "Date of Visit", key: "date_of_visit" },
    { label: "Time In", key: "time_in" },
    { label: "Time Out", key: "time_out" },
    { label: "Duration", key: "duration" },
    { label: "Address", key: "location_address" },
    { label: "Visit Type", key: "type_of_visit" },
    { label: "Psychiatrist", key: "psychiatrist_name" },
    { label: "Rehab Practitioner", key: "rehab_practitioner_name" },
    { label: "Rehab Assistant", key: "rehab_assistant_name" },
    { label: "Medication Adherence", key: "med_adherence" },
    { label: "Created At", key: "created_at" },
  ];

  if (!patientId || !caseId) {
    return <Navigate to="/cases" replace />;
  }

  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Breadcrumbs />
          {canCreateRecord && (
            <button className="btn btn-primary d-flex align-items-center gap-1" onClick={handleAdd}>
              <i className="ri-add-line" /> Add Home Visit Clinical Record
            </button>
          )}
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible" role="alert">
            {typeof error === "string" ? error : "An error occurred fetching records"}
            <button
              type="button"
              className="btn-close"
              onClick={() => dispatch(clearError())}
              aria-label="Close"
            />
          </div>
        )}

        <div className="card shadow-sm border-0">
          <div className="card-header border-bottom py-3 d-flex flex-wrap align-items-center justify-content-between gap-2">
            <h5 className="card-title mb-0">Home Visit Clinical Record History</h5>
            <div className="d-flex align-items-center gap-2">
              <div className="input-group input-group-merge" style={{ width: "250px" }}>
                <span className="input-group-text">
                  <i className="ri-search-line" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search records..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </div>

              <CSVLink
                data={filteredRecords}
                headers={csvHeaders}
                filename="home_visit_clinical_records.csv"
                className="btn btn-outline-secondary d-flex align-items-center gap-1"
              >
                <i className="ri-file-excel-line" /> Export CSV
              </CSVLink>

              <button className="btn btn-outline-danger d-flex align-items-center gap-1" onClick={handleExportPDF}>
                <i className="ri-file-pdf-line" /> Export PDF
              </button>
            </div>
          </div>

          <div className="card-body p-0">
            <DataTable
              columns={columns}
              data={filteredRecords}
              progressPending={isLoading}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              onChangeRowsPerPage={(newSize, page) => {
                setRowsPerPage(newSize);
                setCurrentPage(page);
              }}
              onChangePage={(page) => setCurrentPage(page)}
              paginationComponent={(props) => (
                <CustomPagination
                  {...props}
                  currentPage={currentPage}
                  rowsPerPage={rowsPerPage}
                  rowCount={totalRows}
                />
              )}
              noDataComponent={
                <div className="text-center p-5 text-muted">
                  <i className="ri-file-history-line display-4 d-block mb-2 text-secondary" />
                  No clinical records found for this patient.
                </div>
              }
            />
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default HomeVisitClinicalRecordHome;
